import { useQuery } from "@tanstack/react-query";
import exifr from "exifr";

// Enough to cover the JPEG/PNG header + the standard XMP packet (where GPano's
// ProjectionType lives), without downloading the whole — often large — file.
const HEAD_BYTES = 512 * 1024;

/**
 * Reads image dimensions straight from the file header bytes (JPEG SOF / PNG
 * IHDR), so we can apply the aspect-ratio fallback without decoding — or even
 * fully downloading — the image. Returns null for unsupported/truncated data.
 */
function readImageSize(buffer: ArrayBuffer): { width: number; height: number } | null {
  const view = new DataView(buffer);
  if (view.byteLength < 24) return null;

  // PNG: signature 0x89504E47, then IHDR with width/height at bytes 16..23.
  if (view.getUint32(0) === 0x89504e47) {
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }

  // JPEG: starts with SOI (0xFFD8); walk segment markers to the SOFn frame.
  if (view.getUint16(0) === 0xffd8) {
    let offset = 2;
    while (offset + 9 < view.byteLength) {
      if (view.getUint8(offset) !== 0xff) {
        offset++;
        continue;
      }
      const marker = view.getUint8(offset + 1);
      // SOF0..SOF15, excluding DHT (C4), JPG (C8) and DAC (CC).
      if (
        marker >= 0xc0 &&
        marker <= 0xcf &&
        marker !== 0xc4 &&
        marker !== 0xc8 &&
        marker !== 0xcc
      ) {
        return { height: view.getUint16(offset + 5), width: view.getUint16(offset + 7) };
      }
      const segLength = view.getUint16(offset + 2);
      if (segLength < 2) break;
      offset += 2 + segLength;
    }
  }

  return null;
}

/**
 * Detects whether an image is a 360° (equirectangular) photo.
 *
 * There is no dedicated MIME type for 360 photos — they are plain `image/jpeg`.
 * The signal lives inside the file, so we fetch only its leading bytes and:
 *
 *   1. Look for Google Photo Sphere XMP (`GPano:ProjectionType =
 *      "equirectangular"`) — the authoritative marker, when present.
 *   2. Fall back to an exact 2:1 aspect ratio at large resolution, read from the
 *      file header — catches panoramas whose GPano tag was stripped on export.
 */
async function detectIs360(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { headers: { Range: `bytes=0-${HEAD_BYTES - 1}` } });
    // 206 = partial (Range honored); 200 = server ignored Range (still fine).
    if (!response.ok && response.status !== 206) {
      throw new Error(`fetch failed: ${response.status}`);
    }
    const buffer = await response.arrayBuffer();

    // 1. Authoritative signal: GPano XMP.
    try {
      const xmp = await exifr.parse(buffer, {
        xmp: true,
        multiSegment: true,
        mergeOutput: true,
      });
      const projection = xmp?.ProjectionType ?? xmp?.GPano?.ProjectionType;
      if (projection === "equirectangular") return true;
    } catch {
      // XMP missing or unparseable — fall through to the aspect-ratio heuristic.
    }

    // 2. Heuristic fallback: exact 2:1 at large resolution.
    const dims = readImageSize(buffer);
    if (dims) {
      return Math.abs(dims.width / dims.height - 2) < 0.02 && dims.width >= 2000;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * React Query hook that resolves whether the given image is a 360° panorama.
 * Cached per image so re-opening the lightbox does not re-fetch metadata.
 */
export const useIs360Image = (url?: string, enabled = true) => {
  return useQuery({
    queryKey: ["is-360-image", url],
    queryFn: () => detectIs360(url!),
    enabled: enabled && !!url,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};
